"use client";

import { FormData, isValidEmail, isValidPhone } from "@/lib/form";
import { useQualificationForm } from "@/hooks/useQualificationForm";
import { OptionCard } from "./OptionCard";
import { PhoneField } from "./PhoneField";
import { cn } from "@/lib/utils";

export function QualificationForm({
  onComplete,
}: {
  onComplete: (data: FormData) => void;
}) {
  const {
    data,
    touched,
    contactValid,
    progress,
    currentQcm,
    setField,
    submitContact,
    selectQcm,
    back,
  } = useQualificationForm(onComplete);

  return (
    <div className="mx-auto w-full max-w-md rounded-2xl border border-ink/10 bg-white p-6 text-left shadow-2xl backdrop-blur sm:p-8">
      {/* progress */}
      <div className="mb-6 h-1.5 w-full overflow-hidden rounded-full bg-ink/10">
        <div
          className="h-full rounded-full bg-gold transition-all duration-300"
          style={{ width: `${Math.max(progress, 6)}%` }}
        />
      </div>

      {currentQcm === null ? (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-bold">Votre accès + votre ressource</h2>
            <p className="mt-1 text-sm text-ink/55">
              30 s. On vous ouvre la vidéo tout de suite, et on vous envoie votre
              ressource (copie de la vidéo + note de synthèse) par message et par email.
            </p>
          </div>

          <Field
            label="Prénom et nom"
            value={data.nomComplet}
            onChange={(v) => setField({ nomComplet: v })}
            invalid={touched && data.nomComplet.trim().length <= 2}
          />
          <Field
            label="Email professionnel"
            type="email"
            value={data.email}
            onChange={(v) => setField({ email: v })}
            invalid={touched && !isValidEmail(data.email)}
          />
          <PhoneField
            onChange={(v) => setField({ phone: v })}
            invalid={touched && !isValidPhone(data.phone)}
          />

          {touched && !contactValid && (
            <p className="text-xs text-red-400">
              Merci de renseigner tous les champs avec un email et un téléphone valides.
            </p>
          )}

          <button
            type="button"
            onClick={submitContact}
            className="mt-2 w-full rounded-xl bg-gold px-6 py-3.5 text-sm font-semibold text-ink transition hover:bg-gold-dark"
          >
            Continuer
          </button>
          <p className="text-center text-[11px] text-ink/45">
            Accès immédiat à la vidéo. On vous envoie votre ressource, c’est tout — pas de spam.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <button
            type="button"
            onClick={back}
            className="text-xs text-ink/55 hover:text-ink"
          >
            ← Retour
          </button>
          <h2 className="text-lg font-bold">{currentQcm.question}</h2>
          <div className="space-y-2.5">
            {currentQcm.options.map((opt) => (
              <OptionCard
                key={opt.value}
                label={opt.label}
                selected={data[currentQcm.key] === opt.value}
                onClick={() => selectQcm(currentQcm.key, opt.value)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  invalid = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  invalid?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-ink/55">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "w-full rounded-xl border bg-ink/[0.03] px-3.5 py-3 text-sm text-ink outline-none transition placeholder:text-ink/40 focus:border-gold",
          invalid ? "border-red-500/70" : "border-ink/15"
        )}
      />
    </label>
  );
}
